import { Order } from 'models/order';
import { SubcontractDefaults } from 'models/client-directory';
import {
    SubcontractOrderState,
    SubcontractSelection,
} from 'pages/order-flow/subcontract-order/subcontract-order-context';

/**
 * DD-3484: the subcontractOrder mutation input. Only ids, toggles and the lab's own
 * comment are sent — the server reads the parent order and builds the sub-order from
 * the selection, so the client cannot smuggle unselected content however this input is
 * tampered with.
 */
export type SubcontractOrderMutationInput = {
    parentOrderId: string;
    supplier?: string;
    dentist?: string;
    deliveryDetails: {
        date: Date | null;
        time: { hour?: number; minute?: number };
    };
    selection: {
        restorationIds: string[];
        fileIds: string[];
        scannerCaseFileIds: string[];
        includePatientReference: boolean;
        includePatientIdentityNumber: boolean;
        includeDentistName: boolean;
    };
    comment: string;
    /** DD-3770: absent when the lab chose no impression type. */
    sampleType?: string;
};

/**
 * The parent's patient has an identity number that could travel — a personnummer or a
 * reserve/foreign number (`noPersonalIdentityNumber` only says which kind it is).
 */
export const parentHasIdentityNumber = (parentOrder: Order): boolean =>
    Boolean(parentOrder.patient?.personalIdentityNumber);

/** The parent's dentist's name, when it has one. */
export const parentDentistName = (parentOrder: Order): string => parentOrder.dentist?.fullName?.trim() ?? '';

/**
 * The sub-order comment exactly as it will be sent: the lab's own text, then — when the
 * dentist was selected — the dentist's name as a labelled line. The dentist travels as
 * text only, never as a user reference (a linked dentist would be notified).
 */
export const subcontractCommentOf = (
    parentOrder: Order,
    subcontract: SubcontractOrderState,
    dentistLabel: string,
): string => {
    const dentistName = parentDentistName(parentOrder);
    const dentistLine =
        subcontract.selection.includeDentistName && dentistName ? `${dentistLabel}: ${dentistName}` : '';
    return [subcontract.comment.trim(), dentistLine].filter(Boolean).join('\n\n');
};

export const subcontractOrderInputOf = (
    parentOrder: Order,
    subcontract: SubcontractOrderState,
    dentistLabel: string,
): SubcontractOrderMutationInput => {
    const { selection } = subcontract;
    // A parent without a patient (or dentist) has nothing to include, whatever the toggle says.
    const includePatientReference = selection.includePatientReference && Boolean(parentOrder.patient);
    return {
        parentOrderId: parentOrder._id,
        supplier: subcontract.supplier?._id,
        dentist: subcontract.responsibleUser?._id,
        deliveryDetails: {
            date: subcontract.deliveryDate,
            time: {
                hour: subcontract.deliveryTime.hour,
                minute: subcontract.deliveryTime.minute,
            },
        },
        selection: {
            restorationIds: selection.restorationIds,
            fileIds: selection.fileIds,
            scannerCaseFileIds: selection.scannerCaseFileIds,
            includePatientReference,
            includePatientIdentityNumber:
                includePatientReference &&
                selection.includePatientIdentityNumber &&
                parentHasIdentityNumber(parentOrder),
            includeDentistName: selection.includeDentistName && Boolean(parentDentistName(parentOrder)),
        },
        comment: subcontractCommentOf(parentOrder, subcontract, dentistLabel),
        ...(subcontract.sampleType ? { sampleType: subcontract.sampleType } : {}),
    };
};

/**
 * DD-3485: the selection a partner's defaults pre-fill. A person still reviews and
 * confirms every item in the flow; a partner without defaults pre-selects nothing.
 */
export const selectionFromDefaults = (
    parentOrder: Order,
    defaults: SubcontractDefaults | null | undefined,
): SubcontractSelection => ({
    restorationIds: defaults?.includeAllRestorations
        ? (parentOrder.restorations ?? []).map((restoration) => String(restoration._id))
        : [],
    fileIds: defaults?.includeAttachments ? (parentOrder.filesUrl ?? []).map((file) => file._id) : [],
    scannerCaseFileIds: defaults?.includeScanCaseFiles
        ? (parentOrder.attachedScannerCaseFiles ?? []).flatMap((scannerCase) =>
              (scannerCase.processedFiles ?? []).map((file) => file._id),
          )
        : [],
    includePatientReference: Boolean(defaults?.includePatientReference),
    includePatientIdentityNumber: Boolean(defaults?.includePatientReference && defaults?.includePatientIdentityNumber),
    includeDentistName: Boolean(defaults?.includeDentistName),
});
